import { useQuery, useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { Badge, Box, IconButton } from '@material-ui/core'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import { GET_CLASH_COUNT } from 'api/graphQL/queries/bestPracticeClash.queries'
import {
    bpManagementOpenRequest,
    resetBPStagedConfig,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import ProjectWorkspaceAskDelaChat from 'components/AskDelaChat/ProjectWorkspaceAskDelaChat'
import ChatLauncherIcon from 'components/RightPane/ChatLauncherIcon'
import TrackChangeIcon from 'components/RightPane/TrackChangeIcon'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangesPanel } from 'components/WorkspaceEditor/components/TrackChangesPanel/TrackChangesPanel'
import { useEditorWithSidebar } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks'
import { BPRemoveConfirmationDialog } from 'components/WorkspaceEditor/features/bestPractices/components/BPRemoveConfirmationDialog'
import { BestPracticeManagementPanel } from 'components/WorkspaceEditor/features/bestPractices/components/BestPracticeManagementPanel'
import {
    ClashIcon,
    ClashesPane,
} from 'components/WorkspaceEditor/features/smartProjectCopy'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import {
    canShowBestPracticeSetting,
    isOMBestPracticeFeatureEnabled,
} from 'shared/utils/bestPracticeUtils'
import { ProjectProperties } from './ProjectProperties'
import { ToggleHandlerType } from './SidebarToggles'
import { useShowRightMostPane } from './context/RightMostPaneContext'
import { RightMostPaneTabs } from './enums/RightMostPaneTabs'
import { useRightMostPane } from './hooks/useRightMostPane'
import useRightMostPaneStyles from './styles/useRightMostPaneStyles'

export interface RightMostPaneBetaProps {
    drawingDesignation: string
    showRightMostPane: boolean
    toggleHandler: (
        toggleType: ToggleHandlerType,
        value: boolean | undefined
    ) => void
    contentTeamFromStudioPage?: boolean
    setLoadingBackdrop?: (value: boolean) => void
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    setRightMostPaneTab?: (value: string) => void
    selectedProjectElementNode?: ProjectElementNode | null | undefined
}

export const RightMostPaneBeta = memo(
    ({
        drawingDesignation,
        toggleHandler,
        contentTeamFromStudioPage,
        setLoadingBackdrop,
        setRightMostPaneTab,
        selectedProjectElementNode,
    }: RightMostPaneBetaProps) => {
        const showSpecpointCopilot =
            process.env.REACT_APP_ShowSpecpointCopilot === '1'

        const { projectId } = useParams<{ projectId: string }>()
        const [clashCount, setClashCount] = useState(0)

        const { currentPane, changeTab } = useRightMostPane({
            selectedProjectElementNode,
        })

        const {
            showRightMostPane,
            setShowRightMostPane,
        } = useShowRightMostPane()
        const classes = useRightMostPaneStyles({
            showRightMostPane,
            currentPane,
        })
        const showBPSettings = canShowBestPracticeSetting()
        const { toggleSidebar, isSidebarVisible } = useEditorWithSidebar()
        const { setIsSuggestionsDataUpdated } = useSuggestionsShallowSelector(
            (state) => ({
                setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
            })
        )
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
        const isBPManagementEnabled =
            isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace
        const bpOpenRequest = useReactiveVar(bpManagementOpenRequest)

        // Only show clash icon in AE projects (not OM/SMM) and when at Family/Product level
        // Also requires SmartProjectCopy feature flag to be enabled
        const shouldShowClashIcon =
            process.env.REACT_APP_EnableSmartProjectCopy === '1' &&
            showBPSettings &&
            !contentTeamFromStudioPage &&
            (selectedProjectElementNode?.elementType === 'PRODUCTFAMILY' ||
                selectedProjectElementNode?.elementType === 'PRODUCTTYPE')

        // Query for clash count
        const { data: clashData } = useQuery(GET_CLASH_COUNT, {
            variables: {
                input: {
                    projectId: projectId,
                    treePath: selectedProjectElementNode?.treePath,
                },
            },
            skip: !projectId || !shouldShowClashIcon,
            fetchPolicy: 'cache-and-network',
        })

        useEffect(() => {
            if (clashData?.bestPracticeClashCount !== undefined) {
                setClashCount(clashData.bestPracticeClashCount)
            }
        }, [clashData])

        // Reset all BP-management reactive state when the user navigates to a
        // different project (or unmounts), so a config staged in project A
        // can't apply to project B and the open-request counter doesn't carry
        // an old increment into a fresh mount.
        useEffect(() => {
            return () => {
                resetBPStagedConfig()
                bpManagementOpenRequest(0)
            }
        }, [projectId])

        // Counter (vs payload) keeps this a one-shot per double-click — manual
        // close of the pane does not retrigger because the var doesn't change.
        useEffect(() => {
            if (!isBPManagementEnabled || bpOpenRequest === 0) return
            setShowRightMostPane?.(true)
            changeTab(RightMostPaneTabs.BestPracticeManagement)
            setRightMostPaneTab?.(RightMostPaneTabs.BestPracticeManagement)
        }, [
            isBPManagementEnabled,
            bpOpenRequest,
            setShowRightMostPane,
            changeTab,
            setRightMostPaneTab,
        ])

        const handleTabChange = useCallback(
            (tab: RightMostPaneTabs) => () => {
                const isClosing = currentPane === tab && showRightMostPane

                setShowRightMostPane?.(!isClosing)

                if (tab === RightMostPaneTabs.TrackChanges) {
                    toggleSidebar(!isClosing)
                    setIsSuggestionsDataUpdated?.(!isClosing)
                }

                if (!isClosing) {
                    changeTab(tab)
                    setRightMostPaneTab?.(tab)
                }
            },
            [
                changeTab,
                setShowRightMostPane,
                setRightMostPaneTab,
                setIsSuggestionsDataUpdated,
                toggleSidebar,
                currentPane,
                showRightMostPane,
            ]
        )

        const renderPaneContent = useMemo(() => {
            switch (currentPane) {
                case RightMostPaneTabs.ProjectProperties:
                    return (
                        <ProjectProperties
                            drawingDesignation={drawingDesignation}
                            elementToggleHandler={toggleHandler}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            setLoadingBackdrop={setLoadingBackdrop}
                        />
                    )
                case RightMostPaneTabs.Keynotes:
                    return (
                        <p className={classes.keynoteText}>
                            Key notes will appear here
                        </p>
                    )
                case RightMostPaneTabs.AskDela:
                    return (
                        showSpecpointCopilot && <ProjectWorkspaceAskDelaChat />
                    )

                default:
                    return null
            }
        }, [
            currentPane,
            drawingDesignation,
            toggleHandler,
            contentTeamFromStudioPage,
            setLoadingBackdrop,
            showSpecpointCopilot,
            classes.keynoteText,
            selectedProjectElementNode?.treePath,
        ])

        const renderIconButton = useCallback(
            (
                tab: RightMostPaneTabs,
                label: string,
                IconComponent: React.ElementType,
                extraClassName?: string,
                disableActiveStyle?: boolean
            ) => (
                <HoverTooltip
                    text={label}
                    placements={['left']}
                    className={clsx(extraClassName)}
                >
                    <IconButton
                        size="small"
                        onClick={handleTabChange(tab)}
                        className={clsx(
                            classes.rightPaneButton,
                            !disableActiveStyle &&
                            currentPane === tab &&
                            classes.active
                        )}
                        aria-label={label}
                    >
                        <IconComponent
                            className={
                                currentPane === tab
                                    ? classes.rightPaneIconActive
                                    : classes.rightPaneIcon
                            }
                        />
                    </IconButton>
                </HoverTooltip>
            ),
            [handleTabChange, classes, currentPane]
        )

        const rightMostPane = useMemo(
            () => (
                <div className={classes.buttonContainer}>
                    {showSpecpointCopilot &&
                        renderIconButton(
                            RightMostPaneTabs.AskDela,
                            'Ask Dela',
                            ChatLauncherIcon,
                            classes.askDelaButton,
                            true
                        )}
                    {renderIconButton(
                        RightMostPaneTabs.TrackChanges,
                        'Track Changes',
                        TrackChangeIcon
                    )}
                    {shouldShowClashIcon && (
                        <HoverTooltip
                            text={
                                clashCount > 0
                                    ? `${clashCount} Best Practice clash${clashCount !== 1 ? 'es' : ''
                                    }`
                                    : 'Best Practice Clashes'
                            }
                            placements={['left']}
                        >
                            <IconButton
                                size="small"
                                onClick={handleTabChange(
                                    RightMostPaneTabs.Clashes
                                )}
                                className={clsx(
                                    classes.rightPaneButton,
                                    currentPane === RightMostPaneTabs.Clashes &&
                                    classes.activeClashIcon
                                )}
                                aria-label="Best Practice Clashes"
                            >
                                <Badge
                                    badgeContent={clashCount}
                                    color="error"
                                    max={99}
                                    className={classes.clashIconBadge}
                                    invisible={clashCount === 0}
                                >
                                    <ClashIcon
                                        className={clsx(
                                            classes.rightPaneClashIcon,
                                            currentPane ===
                                                RightMostPaneTabs.Clashes
                                                ? classes.rightPaneIconActive
                                                : classes.rightPaneIcon
                                        )}
                                    />
                                </Badge>
                            </IconButton>
                        </HoverTooltip>
                    )}
                    {isBPManagementEnabled &&
                        renderIconButton(
                            RightMostPaneTabs.BestPracticeManagement,
                            'Best Practice Management',
                            VerifiedUserOutlinedIcon
                        )}
                </div>
            ),
            [
                showSpecpointCopilot,
                renderIconButton,
                classes,
                shouldShowClashIcon,
                clashCount,
                handleTabChange,
                currentPane,
                isBPManagementEnabled,
            ]
        )

        let paneContent: React.ReactNode
        if (currentPane === RightMostPaneTabs.TrackChanges) {
            paneContent = (
                <Box
                    id="trackChangesPanelId"
                    className={clsx(
                        classes.trackChangesPanel,
                        isSidebarVisible || showRightMostPane
                            ? classes.trackChangesPanelExpanded
                            : classes.trackChangesPanelCollapsed
                    )}
                    display="flex"
                    height="100%"
                >
                    <TrackChangesPanel
                        isSidebarVisible={isSidebarVisible || showRightMostPane}
                    />
                    {rightMostPane}
                </Box>
            )
        } else if (
            currentPane === RightMostPaneTabs.Clashes &&
            shouldShowClashIcon
        ) {
            paneContent = (
                <Box
                    id="trackChangesPanelId"
                    className={clsx(
                        classes.trackChangesPanel,
                        isSidebarVisible || showRightMostPane
                            ? classes.trackChangesPanelExpanded
                            : classes.trackChangesPanelCollapsed
                    )}
                    display="flex"
                    height="100%"
                >
                    <ClashesPane
                        treePath={selectedProjectElementNode?.treePath}
                        isSidebarVisible={isSidebarVisible || showRightMostPane}
                    />
                    {rightMostPane}
                </Box>
            )
        } else if (
            isBPManagementEnabled &&
            currentPane === RightMostPaneTabs.BestPracticeManagement
        ) {
            paneContent = (
                <Box
                    id="bestPracticeManagementPanelId"
                    className={clsx(
                        classes.trackChangesPanel,
                        isSidebarVisible || showRightMostPane
                            ? classes.trackChangesPanelExpanded
                            : classes.trackChangesPanelCollapsed
                    )}
                    display="flex"
                    height="100%"
                >
                    <BestPracticeManagementPanel
                        isSidebarVisible={isSidebarVisible || showRightMostPane}
                    />
                    {rightMostPane}
                </Box>
            )
        } else {
            paneContent = (
                <div
                    className={classes.fullWidthRightPaneKeynotesContainer}
                    id="rightPanelId"
                >
                    {showRightMostPane && (
                        <div
                            className={classes.rightPaneInnerContainer}
                            id="rightMostPaneId"
                        >
                            {renderPaneContent}
                        </div>
                    )}
                    {rightMostPane}
                </div>
            )
        }

        return (
            <>
                {paneContent}
                {isBPManagementEnabled && <BPRemoveConfirmationDialog />}
            </>
        )
    }
)
